import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  AppNotification,
  NotificationFilter,
  NotificationsSnapshot,
  categoryIcon,
  categoryLabel,
  formatNotificationTime,
} from '../../../service/admin/notification.model';
import { NotificationSoundService } from '../../../service/admin/notification-sound.service';
import { NotificationsService } from '../../../service/admin/notifications.service';

@Component({
  selector: 'app-notifications-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notifications-panel.component.html',
  styleUrl: './notifications-panel.component.scss',
})
export class NotificationsPanelComponent implements OnInit, OnDestroy {
  @Input() open = false;
  @Output() openChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<void>();

  snapshot: NotificationsSnapshot = {
    items: [],
    unreadCount: 0,
    loading: true,
    lastUpdatedAt: null,
    error: null,
  };
  filter: NotificationFilter = 'all';
  soundMuted = false;
  private audioReady = false;

  readonly filters: { id: NotificationFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'action', label: 'Action' },
    { id: 'queue', label: 'Queue' },
    { id: 'payments', label: 'Pay' },
  ];

  private readonly subs = new Subscription();

  constructor(
    private notifications: NotificationsService,
    private sound: NotificationSoundService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.soundMuted = this.sound.isMuted();
    this.notifications.start();
    this.subs.add(
      this.notifications.snapshot$.subscribe((snapshot) => {
        this.snapshot = snapshot;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @HostListener('document:pointerdown')
  onFirstGesture(): void {
    if (this.audioReady) {
      return;
    }
    this.sound.unlock();
    this.audioReady = true;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.close();
    }
  }

  get filteredItems(): AppNotification[] {
    const items = this.snapshot.items || [];
    switch (this.filter) {
      case 'action':
        return items.filter((item) => item.priority === 'high' || item.category === 'registration');
      case 'queue':
        return items.filter((item) => item.category === 'queue' || item.category === 'pharmacy');
      case 'payments':
        return items.filter((item) => item.category === 'payment');
      default:
        return items;
    }
  }

  get filterCounts(): Record<NotificationFilter, number> {
    const items = this.snapshot.items || [];
    return {
      all: items.length,
      action: items.filter((item) => item.priority === 'high' || item.category === 'registration').length,
      queue: items.filter((item) => item.category === 'queue' || item.category === 'pharmacy').length,
      payments: items.filter((item) => item.category === 'payment').length,
    };
  }

  toggle(event?: Event): void {
    event?.stopPropagation();
    this.sound.unlock();
    this.audioReady = true;
    const next = !this.open;
    this.open = next;
    this.openChange.emit(next);
    if (next) {
      this.notifications.refresh();
    }
  }

  close(): void {
    this.open = false;
    this.openChange.emit(false);
    this.closed.emit();
  }

  setFilter(filter: NotificationFilter, event?: Event): void {
    event?.stopPropagation();
    this.filter = filter;
  }

  refresh(event?: Event): void {
    event?.stopPropagation();
    this.notifications.refresh();
  }

  markAllRead(event?: Event): void {
    event?.stopPropagation();
    this.notifications.markAllRead();
  }

  markItemRead(item: AppNotification, event: Event): void {
    event.stopPropagation();
    this.notifications.markRead(item.id);
  }

  toggleSound(event?: Event): void {
    event?.stopPropagation();
    this.sound.unlock();
    this.audioReady = true;
    this.soundMuted = this.sound.toggleMuted();
    if (!this.soundMuted) {
      void this.sound.preview(this.resolveRole());
    }
  }

  openItem(item: AppNotification, event?: Event): void {
    event?.stopPropagation();
    this.notifications.markRead(item.id);
    this.close();
    if (item.path) {
      this.router.navigateByUrl(item.path);
    }
  }

  dismissItem(item: AppNotification, event: Event): void {
    event.stopPropagation();
    this.notifications.dismiss(item.id);
  }

  iconFor(item: AppNotification): string {
    return categoryIcon(item.category);
  }

  labelFor(item: AppNotification): string {
    return categoryLabel(item.category);
  }

  timeFor(item: AppNotification): string {
    return formatNotificationTime(item.createdAt);
  }

  get updatedLabel(): string {
    return formatNotificationTime(this.snapshot.lastUpdatedAt);
  }

  private resolveRole(): string {
    try {
      return String(JSON.parse(sessionStorage.getItem('loggedInUser') || '{}')?.type || '');
    } catch {
      return '';
    }
  }
}
